/**
 * Scanned contract reference parsing — the scanner input boundary.
 *
 * Every pledged object carries a label, and an operator finds a contract by scanning
 * one. Three shapes can arrive at a counter:
 *
 * | Shape | Example | Origin | Lifecycle |
 * |---|---|---|---|
 * | Legacy | `--321679` | DYMO labels printed before this application took over | **Temporary** — see `barcodeInput.ts` |
 * | Canonical | `321679 - 06.07.2026/A` | every label this application prints, Zebra and DYMO | **Permanent** |
 * | Bare number | `321679` | an operator typing into a field that invites a number | **Permanent** |
 *
 * **Everything here is fully anchored and validated.** The previous behaviour relied
 * on `parseInt` stopping at the first non-digit, which accepted a canonical reference
 * only by accident and accepted `123abc` as contract 123 by the same accident. Both
 * are now decided deliberately: the first is a supported format, the second is
 * rejected.
 *
 * **The canonical grammar is derived, not guessed.** It mirrors
 * `backend/src/domain/labelPrinting/contractReference.ts`, which is the one formatter
 * that composes what gets printed, and `__fixtures__/contractReferenceSamples.json`
 * carries real output from it. This module's tests parse that fixture, and a backend
 * test asserts the formatter still produces exactly those strings — so a format change
 * breaks a test rather than quietly breaking every scanner in the estate.
 *
 * Pure: no I/O, no state.
 */

import { normalizeScannedBarcode } from './barcodeInput';

/**
 * Which of the three shapes an input turned out to be.
 *
 * Reported so a caller can treat them differently — the label reprint uses it to tell
 * "this exact object's tag was scanned" from "a contract number was typed".
 */
export type ContractReferenceSource =
    /** A label printed by this application: contract, date and object letter. */
    | 'canonical'
    /** A label printed before the migration: the `--` sentinel and a contract number. */
    | 'legacy'
    /** A contract number entered by hand. */
    | 'manual';

/** What a scan or a typed value resolved to. */
export interface ParsedContractReference {
    /** Contract number, validated as a positive integer. */
    contractNumber: number;
    /**
     * Which pledged object of the contract the label belongs to, when the input
     * identified one. Only a canonical reference carries this — a contract number,
     * however it arrived, identifies the whole contract.
     */
    objectLetter?: string;
    /**
     * The date exactly as it is printed, `dd.MM.yyyy`, when the input carried one.
     *
     * Preserved rather than reformatted. It identifies nothing on its own — the
     * contract number and letter already do that — but it is what an operator reads
     * off the tag, so it belongs in any message that quotes back what was scanned.
     */
    printedDate?: string;
    /** Which shape the input took. */
    source: ContractReferenceSource;
}

/** Why an input could not be resolved. */
export type ContractReferenceFailure =
    /** Nothing was entered, or only a scanner terminator arrived. */
    | 'empty'
    /** Something was entered, but it is not one of the accepted shapes. */
    | 'malformed'
    /** A bare number was entered where only a scanned label is accepted. */
    | 'bare-number-not-accepted';

/** Outcome of parsing. A discriminated union, so a caller cannot read a value it did not get. */
export type ContractReferenceResult =
    | { ok: true; value: ParsedContractReference }
    | { ok: false; reason: ContractReferenceFailure };

/** Options a call site uses to state what its field accepts. */
export interface ParseContractReferenceOptions {
    /**
     * Whether a bare contract number is acceptable.
     *
     * True for the fields that invite an operator to type a number — which is all
     * three today. Stated per call site rather than assumed, so a future scan-only
     * field does not silently accept typed input.
     */
    allowBareNumber?: boolean;
}

/**
 * Grammar fragments, mirroring `CONTRACT_REFERENCE_PARTS` in the backend formatter.
 *
 * Kept as named fragments rather than one opaque expression so each part can be read
 * against the formatter it comes from, and so the anchoring is visible at the point
 * the patterns are assembled below.
 */
const PART = {
    /** Contract number: digits only. Bounded so a scan cannot produce an unsafe integer. */
    contractNumber: '\\d{1,9}',
    /** Print date, `dd.MM.yyyy`. Range-checked separately — the shape alone allows 31.02. */
    printedDate: '(\\d{2})\\.(\\d{2})\\.(\\d{4})',
    /** Object letter: `A`…`Z`, then `AA`… as the label builder rolls over. */
    objectLetter: '[A-Z]{1,3}',
} as const;

/** `321679 - 06.07.2026/A` — anchored at both ends. */
const CANONICAL_PATTERN = new RegExp(
    `^(${PART.contractNumber}) - ${PART.printedDate}/(${PART.objectLetter})$`,
);

/** `321679` — anchored at both ends. */
const BARE_NUMBER_PATTERN = new RegExp(`^(${PART.contractNumber})$`);

/** Highest month number, for the date range check. */
const MONTHS_IN_YEAR = 12;

/** Earliest year a printed label could plausibly carry. */
const EARLIEST_PRINTED_YEAR = 1990;

/** Latest year a printed label could plausibly carry. */
const LATEST_PRINTED_YEAR = 2200;

/**
 * Check that a `dd.MM.yyyy` date is a real calendar date.
 *
 * The shape check alone accepts `31.02.2026`. Round-tripping through `Date` rejects
 * it, because a rolled-over day comes back as a different month.
 */
function isRealDate(day: number, month: number, year: number): boolean {
    if (month < 1 || month > MONTHS_IN_YEAR || day < 1) {
        return false;
    }

    if (year < EARLIEST_PRINTED_YEAR || year > LATEST_PRINTED_YEAR) {
        return false;
    }

    const candidate = new Date(year, month - 1, day);

    return (
        candidate.getFullYear() === year &&
        candidate.getMonth() === month - 1 &&
        candidate.getDate() === day
    );
}

/**
 * Resolve a scanned or typed value to a contract, and to an object where the input
 * identified one.
 *
 * The input is trimmed of the CR/LF a keyboard-wedge scanner appends and of the legacy
 * `--` sentinel — see {@link normalizeScannedBarcode} — and then matched **whole**
 * against the accepted shapes. Nothing is accepted on a prefix.
 *
 * @param raw Text as the scanner or the operator produced it.
 * @param options What this particular field accepts.
 */
export function parseContractReference(
    raw: string | null | undefined,
    options: ParseContractReferenceOptions = {},
): ContractReferenceResult {
    const carriedSentinel =
        typeof raw === 'string' && raw.trim().startsWith('--');

    const value = normalizeScannedBarcode(raw);

    if (value === '') {
        // A bare sentinel is a scan that carried no payload, which is a different problem
        // from an operator not having typed anything — and deserves a different message.
        return { ok: false, reason: carriedSentinel ? 'malformed' : 'empty' };
    }

    const canonical = CANONICAL_PATTERN.exec(value);

    if (canonical !== null) {
        const [, contractNumber, day, month, year, objectLetter] = canonical;

        if (!isRealDate(Number(day), Number(month), Number(year))) {
            return { ok: false, reason: 'malformed' };
        }

        const parsed = toContractNumber(contractNumber);

        return parsed === null
            ? { ok: false, reason: 'malformed' }
            : {
                  ok: true,
                  value: {
                      contractNumber: parsed,
                      objectLetter,
                      printedDate: `${day}.${month}.${year}`,
                      source: 'canonical',
                  },
              };
    }

    const bare = BARE_NUMBER_PATTERN.exec(value);

    if (bare === null) {
        return { ok: false, reason: 'malformed' };
    }

    const parsed = toContractNumber(bare[1]);

    if (parsed === null) {
        return { ok: false, reason: 'malformed' };
    }

    // A legacy label is a scan, not typed input, so it is accepted whatever the field
    // says about bare numbers. Only a value that arrived without the sentinel is
    // subject to that setting.
    if (carriedSentinel) {
        return { ok: true, value: { contractNumber: parsed, source: 'legacy' } };
    }

    if (options.allowBareNumber === false) {
        return { ok: false, reason: 'bare-number-not-accepted' };
    }

    return { ok: true, value: { contractNumber: parsed, source: 'manual' } };
}

/** Convert a matched digit run to a positive contract number, or `null`. */
function toContractNumber(digits: string): number | null {
    const value = Number(digits);

    // `\d{1,9}` cannot overflow, so the only rejection left is zero — there is no
    // contract 0, and the lookups already refused it.
    return Number.isSafeInteger(value) && value > 0 ? value : null;
}
